import { useState } from 'react';
import type { AgreementSubtype } from '@catalyst-psa/shared';
import { DEFAULT_PAGE_SIZE } from '@catalyst-psa/shared';

import { useAuth } from '../../auth/authContext.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { Icon } from '../../components/ui/Icon.js';
import { Pagination } from '../../components/ui/Pagination.js';
import { AgreementSubtypeFormModal } from './AgreementSubtypeFormModal.js';
import { useAgreementSubtypes } from './api.js';

export function AgreementSubtypesPage(): JSX.Element {
  const { isAdmin } = useAuth();
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AgreementSubtype | null>(null);

  const query = useAgreementSubtypes({ page, pageSize: DEFAULT_PAGE_SIZE, sortBy: 'sortOrder' });
  const subtypes = query.data?.data ?? [];
  const pageInfo = query.data?.page;

  const openCreate = (): void => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (subtype: AgreementSubtype): void => {
    setEditing(subtype);
    setFormOpen(true);
  };

  return (
    <div>
      <PageHeader
        title="Agreement Subtypes"
        description="Configure the descriptive list of agreement subtypes. These do not affect billing."
        actions={
          isAdmin ? (
            <Button onClick={openCreate}>
              <Icon name="agreements" className="h-4 w-4" />
              New subtype
            </Button>
          ) : undefined
        }
      />

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 font-medium">Order</th>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={3}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={subtypes.length === 0}
              emptyTitle="No agreement subtypes found"
              emptyDescription="Create your first agreement subtype to get started."
              emptyAction={isAdmin ? <Button onClick={openCreate}>New subtype</Button> : undefined}
            >
              {subtypes.map((subtype) => (
                <tr
                  key={subtype.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => openEdit(subtype)}
                >
                  <td className="px-4 py-3 tabular-nums text-slate-500">{subtype.sortOrder}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{subtype.name}</td>
                  <td className="px-4 py-3">
                    <Badge tone={subtype.isActive ? 'green' : 'gray'}>
                      {subtype.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>

        {pageInfo && subtypes.length > 0 ? (
          <Pagination
            page={pageInfo.page}
            pageSize={pageInfo.pageSize}
            totalItems={pageInfo.totalItems}
            totalPages={pageInfo.totalPages}
            onPageChange={setPage}
          />
        ) : null}
      </div>

      <AgreementSubtypeFormModal
        isOpen={formOpen}
        subtype={editing}
        onClose={() => setFormOpen(false)}
      />
    </div>
  );
}
